import React, { useState } from "react";
import { useLazyQuery } from "@apollo/client/react";
import { useNavigate } from "react-router";
import Link from "@mui/material/Link";
import IconButton from "@mui/material/IconButton";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import HistoryIcon from "@mui/icons-material/History";
import PopupState, { bindMenu, bindTrigger } from "material-ui-popup-state";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import DeleteIcon from "@mui/icons-material/Delete";
import InventoryOutlinedIcon from "@mui/icons-material/InventoryOutlined";
import PdfReportMenuItem from "../../../components/PdfReportMenuItem";
import useAuth from "../../../hooks/useAuth";
import useOpenSignedPdfUrl from "../../../hooks/useOpenSignedPdfUrl";
import useQueryParams from "../../../hooks/useQuery";
import useRoles from "../../../hooks/user/useRoles";
import { useProject } from "../ProjectProvider";
import DeleteShipmentModal from "./DeleteShipmentModal";
import LabelingOptionsModal from "../../../components/modals/LabelingOptionsModal";
import useItemLabelsModal from "../../../hooks/useItemLabelsModal";
import InboundChargesSummaryModal from "./InboundChargesSummaryModal";
import NotifyCustomerModal from "./NotifyCustomerModal";
import RevisionLogModal from "../../RevisionLogModal";
import { GET_SHIPMENT_QUERY } from "../../../constants/graphql/queries";
import { shipmentHasMobileLocationLabels } from "../../../utils/shipments";

const useSafeProject = () => {
  try {
    return useProject();
  } catch (error) {
    return { project: null, client: null };
  }
};

export default function InboundActions({ item, onEdit, onRemove, variables }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const query = useQueryParams();
  const { settings } = user.company;
  const { project: contextProject } = useSafeProject();
  const project = item.project || contextProject; // Prefer shipment's project, then context
  const [removeItem, setRemoveItem] = useState(false);
  const itemLabelsModal = useItemLabelsModal();
  const [openNotifyCustomerModal, setOpenNotifyCustomerModal] = useState(false);
  const [fetchShipment, { data: shipmentData }] = useLazyQuery(GET_SHIPMENT_QUERY, {
    fetchPolicy: "network-only",
  });
  const billingEnabled = !!project?.billing;
  const isRestrictedRole = useRoles(["employee", "viewer"]);
  const isAdminOrManager = useRoles(["admin", "manager"]);
  const alreadyNotified = !!item.notifiedAt;
  const hasApprovedEmails =
    !!project?.approvedEmailSendList?.trim() ||
    !!item?.costCenter?.approvedEmailSendList?.trim() ||
    !!item?.requiresNotification;
  const hasMobileLocationLabels = shipmentHasMobileLocationLabels(shipmentData?.shipment || item);
  const inboundChargesSummaryId = query.get("inboundChargesSummaryId");
  const transferShipmentId = query.get("transferShipmentId");
  const shipmentRevisionLogId = query.get("shipmentRevisionLogId");
  const openChargesSummaryModal = inboundChargesSummaryId && inboundChargesSummaryId === item.id;
  const openTransferModal = transferShipmentId && transferShipmentId === item.id;
  const openRevisionLog = shipmentRevisionLogId && shipmentRevisionLogId === item.id;
  const openPdfReport = useOpenSignedPdfUrl({
    resourceType: "shipment_pdfs",
    resourceId: item.id,
  });
  const openRegeneratedPdfReport = useOpenSignedPdfUrl({
    resourceType: "shipment_pdfs",
    resourceId: item.id,
    extraQueryParams: { force_pdf_regeneration: true },
  });
  const openLocationLabelsPdf = useOpenSignedPdfUrl({
    resourceType: "shipment_mobile_location_labels_pdfs",
    resourceId: item.id,
  });
  const openRegeneratedLocationLabelsPdf = useOpenSignedPdfUrl({
    resourceType: "shipment_mobile_location_labels_pdfs",
    resourceId: item.id,
    extraQueryParams: { force_pdf_regeneration: true },
  });

  const toggleRemoveModal = () => {
    setRemoveItem(v => !v);
  };

  const handleRemoveShipment = async (deleteCorrespondingAssets = false) => {
    await onRemove(deleteCorrespondingAssets);
    setRemoveItem(false);
  };

  const toggleTransferModal = () => {
    const searchParams = new URLSearchParams(window.location.search);
    if (openTransferModal) {
      searchParams.delete("transferShipmentId");
      searchParams.delete("transferStep");
    } else {
      searchParams.set("transferShipmentId", item.id);
      searchParams.set("transferStep", "intro");
    }
    navigate({ search: searchParams.toString() });
  };

  const toggleChargesSummaryModal = () => {
    const searchParams = new URLSearchParams(window.location.search);
    if (openChargesSummaryModal) {
      searchParams.delete("inboundChargesSummaryId");
    } else {
      searchParams.set("inboundChargesSummaryId", item.id);
    }
    navigate({ search: searchParams.toString() });
  };

  const toggleNotifyCustomerModal = () => {
    setOpenNotifyCustomerModal(v => !v);
  };

  const toggleRevisionLog = () => {
    const searchParams = new URLSearchParams(window.location.search);
    if (openRevisionLog) {
      searchParams.delete("shipmentRevisionLogId");
    } else {
      searchParams.set("shipmentRevisionLogId", item.id);
    }
    navigate({ search: searchParams.toString() });
  };

  const handleMenuOpen = (event, popupState) => {
    if (!shipmentHasMobileLocationLabels(item)) {
      fetchShipment({ variables: { id: item.id } });
    }

    bindTrigger(popupState).onClick(event);
  };

  return (
    <>
      {!isRestrictedRole && (
        <Tooltip title="Edit Shipment">
          <IconButton onClick={() => onEdit(item)}>
            <EditOutlinedIcon color="action" />
          </IconButton>
        </Tooltip>
      )}
      <PopupState variant="popover" popupId="popup-menu">
        {popupState => {
          const triggerProps = bindTrigger(popupState);

          return (
            <React.Fragment>
              <Tooltip title="More Options">
                <IconButton
                  {...triggerProps}
                  onClick={(event) => handleMenuOpen(event, popupState)}
                >
                  <MoreVertIcon color="action" />
                </IconButton>
              </Tooltip>
              <Menu {...bindMenu(popupState)}>
                <PdfReportMenuItem
                  onOpen={openPdfReport}
                  onRegenerate={openRegeneratedPdfReport}
                  onClose={popupState.close}
                />
                <MenuItem
                  onClick={() => {
                    popupState.close();
                    itemLabelsModal.openForShipment(item.id);
                  }}
                >
                  <ListItemIcon>
                    <InventoryOutlinedIcon />
                  </ListItemIcon>
                  <ListItemText primary="Item Labels" />
                </MenuItem>
                {hasMobileLocationLabels && (
                  <PdfReportMenuItem
                    label="Location Labels"
                    regenerateLabel="Force Regenerate Location Labels"
                    icon={<InventoryOutlinedIcon />}
                    onOpen={openLocationLabelsPdf}
                    onRegenerate={openRegeneratedLocationLabelsPdf}
                    onClose={popupState.close}
                  />
                )}
                {!isRestrictedRole && (
                  <MenuItem
                    onClick={() => {
                      popupState.close();
                      toggleTransferModal();
                    }}
                  >
                    <ListItemIcon>
                      <SwapHorizIcon />
                    </ListItemIcon>
                    <ListItemText primary="Transfer" />
                  </MenuItem>
                )}
                {billingEnabled && isAdminOrManager && (
                  <MenuItem
                    onClick={() => {
                      popupState.close();
                      toggleChargesSummaryModal();
                    }}
                  >
                    <ListItemIcon>
                      <RequestQuoteIcon />
                    </ListItemIcon>
                    <ListItemText primary="Inbound Charges Summary" />
                  </MenuItem>
                )}
                {hasApprovedEmails && !isRestrictedRole && !alreadyNotified && (
                  <MenuItem
                    onClick={() => {
                      popupState.close();
                      toggleNotifyCustomerModal();
                    }}
                  >
                    <ListItemIcon>
                      <NotificationsActiveIcon />
                    </ListItemIcon>
                    <ListItemText primary="Approve and Notify Customer" />
                  </MenuItem>
                )}
                <MenuItem
                  onClick={() => {
                    popupState.close();
                    toggleRevisionLog();
                  }}
                >
                  <ListItemIcon>
                    <HistoryIcon />
                  </ListItemIcon>
                  <ListItemText primary="Revision Log" />
                </MenuItem>
                {isAdminOrManager && (
                  <MenuItem
                    onClick={() => {
                      toggleRemoveModal();
                      popupState.close();
                    }}
                  >
                    <ListItemIcon>
                      <DeleteIcon />
                    </ListItemIcon>
                    <ListItemText primary="Delete Shipment" />
                  </MenuItem>
                )}
              </Menu>
            </React.Fragment>
          );
        }}
      </PopupState>
      {removeItem && (
        <DeleteShipmentModal
          title="Delete Shipment"
          descr="Are you sure want to delete this shipment?"
          onClose={toggleRemoveModal}
          onDelete={handleRemoveShipment}
          shipment={item}
        />
      )}
      {itemLabelsModal.isOpen && itemLabelsModal.shipmentId === item.id && (
        <LabelingOptionsModal
          onClose={itemLabelsModal.close}
          shipmentId={item.id}
          project={project}
        />
      )}
      {billingEnabled && openChargesSummaryModal && (
        <InboundChargesSummaryModal
          shipment={item}
          project={project}
          onClose={toggleChargesSummaryModal}
        />
      )}
      {openNotifyCustomerModal && (
        <NotifyCustomerModal
          shipment={item}
          onClose={toggleNotifyCustomerModal}
          variables={variables}
        />
      )}
      {openRevisionLog && (
        <RevisionLogModal itemId={item.id} itemType="Shipment" onClose={toggleRevisionLog} />
      )}
    </>
  );
}
